import type { LatLng } from '@/core/utils';
import { DEG, WGS84_A, WGS84_E2 } from './wgs84';

/** Universal Polar Stereographic (used by MGRS above 84°N / below 80°S). */
export function upsToLatLng(north: boolean, easting: number, northing: number): LatLng {
  const e = Math.sqrt(WGS84_E2);
  const k0 = 0.994;
  const x = easting - 2e6;
  const y = northing - 2e6;
  const t = (Math.hypot(x, y) * Math.sqrt((1 + e) ** (1 + e) * (1 - e) ** (1 - e))) / (2 * WGS84_A * k0);
  let phi = Math.PI / 2 - 2 * Math.atan(t);
  for (let i = 0; i < 8; i++) {
    phi = Math.PI / 2 - 2 * Math.atan(t * ((1 - e * Math.sin(phi)) / (1 + e * Math.sin(phi))) ** (e / 2));
  }
  return north
    ? { lat: phi * DEG, lng: Math.atan2(x, -y) * DEG }
    : { lat: -phi * DEG, lng: Math.atan2(x, y) * DEG };
}

type PolarBand = 'A' | 'B' | 'Y' | 'Z';

/** [lowest column letter, highest column letter, highest row letter, false easting, false northing] */
const POLAR_BANDS: Record<PolarBand, [string, string, string, number, number]> = {
  A: ['J', 'Z', 'Z', 800000, 800000],
  B: ['A', 'R', 'Z', 2000000, 800000],
  Y: ['J', 'Z', 'P', 800000, 1300000],
  Z: ['A', 'J', 'P', 2000000, 1300000],
};

/** Polar MGRS, e.g. "ZAF0193788990". Letter scheme from NGA GeoTrans. */
export function upsMgrsToLatLng(band: PolarBand, col: string, row: string, digits: string): LatLng | null {
  const idx = (c: string) => c.charCodeAt(0) - 65;
  const [colLow, colHigh, rowHigh, falseE, falseN] = POLAR_BANDS[band];
  if (col < colLow || col > colHigh || row > rowHigh || 'DEMNVW'.includes(col)) return null;
  let n = idx(row) * 1e5 + falseN;
  if (row > 'I') n -= 1e5;
  if (row > 'O') n -= 1e5;
  let e = (idx(col) - idx(colLow)) * 1e5 + falseE;
  if (colLow !== 'A') {
    if (col > 'L') e -= 3e5;
    if (col > 'U') e -= 2e5;
  } else {
    if (col > 'C') e -= 2e5;
    if (col > 'I') e -= 1e5;
    if (col > 'L') e -= 3e5;
  }
  const half = digits.length / 2;
  const unit = 10 ** (5 - half);
  e += Number(digits.slice(0, half) || 0) * unit + unit / 2;
  n += Number(digits.slice(half) || 0) * unit + unit / 2;
  return upsToLatLng(band >= 'Y', e, n);
}
